import { FeeDefinition, FieldType } from '@pmp/api';
import { CrudMode, FormItem, SelectsOptions, transformUppercaseNoSpaces } from '@ohpen/enso-ui';
import type { RenderFormItem } from '@ohpen/enso-ui';
import { t } from 'i18next';

export const getFeesFormSchema = (
  selectsOptions: SelectsOptions,
  renderFeeBands: RenderFormItem<FeeDefinition>,
  renderAlert: RenderFormItem<FeeDefinition>,
  mode?: CrudMode | null,
  isUsed?: boolean | null,
): FormItem[] => {
  const disabled = mode === CrudMode.VIEW || isUsed;

  return [
    {
      renderFormItem: renderAlert,
    },
    {
      title: 'pmp.fees.labels.feeId',
      dataIndex: 'feeId',
      formItemProps: {
        required: true,
        getValueFromEvent: transformUppercaseNoSpaces,
      },
      fieldProps: {
        disabled: mode !== CrudMode.CREATE,
      },
    },
    {
      title: 'pmp.fees.labels.feeName',
      dataIndex: 'feeName',
      tooltip: t('pmp.fees.labels.feeNameHint'),
      formItemProps: {
        required: true,
      },
      fieldProps: {
        disabled: mode === CrudMode.VIEW,
      },
    },
    {
      title: 'pmp.fees.labels.description',
      dataIndex: 'description',
      tooltip: t('pmp.fees.labels.descriptionHint'),
      formItemProps: {
        required: true,
      },
      fieldProps: {
        disabled: mode === CrudMode.VIEW,
      },
    },
    {
      title: 'pmp.fees.labels.feeType',
      dataIndex: 'feeType',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        options: selectsOptions[FieldType.FEE_TYPE],
        disabled,
      },
    },
    {
      title: 'pmp.fees.labels.calculationType',
      dataIndex: 'calculationType',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        options: selectsOptions[FieldType.CALCULATION_TYPE],
        disabled,
      },
    },
    {
      title: 'pmp.fees.labels.amount',
      dataIndex: 'amount',
      valueType: 'digit',
      dependencies: ['calculationType'],
      fieldProps: {
        disabled,
        addonBefore: '£',
      },
      formItemProps: form => {
        const calculationType = form.getFieldValue('calculationType');
        const isFixed = calculationType === 'FIXEDAMOUNT' || calculationType?.key === 'FIXEDAMOUNT';
        return {
          required: isFixed,
          hidden: !isFixed,
        };
      },
    },
    {
      title: 'pmp.fees.labels.percentage',
      dataIndex: 'percentage',
      valueType: 'percent',
      dependencies: ['calculationType'],
      fieldProps: {
        disabled,
      },
      formItemProps: form => {
        const calculationType = form.getFieldValue('calculationType');
        const isPercentage = calculationType === 'PERCENTAGE' || calculationType?.key === 'PERCENTAGE';
        return {
          required: isPercentage,
          hidden: !isPercentage,
        };
      },
    },
    {
      title: 'pmp.fees.labels.feeBands',
      dataIndex: 'bands',
      renderFormItem: renderFeeBands,
      dependencies: ['calculationType'],
      fieldProps: {
        disabled,
      },
      formItemProps: form => {
        const calculationType = form.getFieldValue('calculationType');
        const isBanded = calculationType === 'BANDEDFIXEDAMOUNT' || calculationType?.key === 'BANDEDFIXEDAMOUNT';
        return {
          required: isBanded,
          hidden: !isBanded,
        };
      },
    },
    {
      title: 'pmp.fees.labels.basedOn',
      dataIndex: 'basedOn',
      valueType: 'select',
      dependencies: ['calculationType'],
      fieldProps: {
        options: selectsOptions[FieldType.PRODUCT_FEE_BASED_ON],
        disabled,
      },
      formItemProps: form => {
        const calculationType = form.getFieldValue('calculationType');
        const isPercentage = calculationType === 'PERCENTAGE' || calculationType?.key === 'PERCENTAGE';
        return {
          required: isPercentage,
          hidden: !isPercentage,
        };
      },
    },
    {
      title: 'pmp.fees.labels.paymentMethods',
      dataIndex: 'paymentMethods',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        mode: 'multiple',
        options: selectsOptions[FieldType.PAYMENT_METHOD],
        disabled,
      },
    },
    {
      title: 'pmp.fees.labels.paymentStages',
      dataIndex: 'paymentStages',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        mode: 'multiple',
        options: selectsOptions[FieldType.PAYMENT_STAGE],
        disabled,
      },
    },
    {
      title: 'pmp.fees.labels.accountCode',
      dataIndex: 'accountCode',
      fieldProps: {
        disabled,
      },
    },
    {
      title: 'pmp.fees.labels.costCentre',
      dataIndex: 'costCentre',
      fieldProps: {
        disabled,
      },
    },
    {
      title: 'pmp.fees.labels.refundable',
      dataIndex: 'refundable',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        options: selectsOptions[FieldType.YES_NO],
        disabled,
      },
    },
    {
      title: 'pmp.fees.labels.waivable',
      dataIndex: 'waivable',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        options: selectsOptions[FieldType.YES_NO],
        disabled,
      },
    },
    {
      title: 'pmp.fees.labels.estimated',
      dataIndex: 'estimated',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        options: selectsOptions[FieldType.YES_NO],
        disabled,
      },
    },
  ];
};

export const getFeeProfilesFormSchema = (
  selectsOptions: SelectsOptions,
  renderAlert: RenderFormItem<FeeDefinition>,
  mode?: CrudMode | null,
  isUsed?: boolean | null,
): FormItem[] => {
  const disabled = mode === CrudMode.VIEW || isUsed;

  return [
    {
      renderFormItem: renderAlert,
    },
    {
      title: 'pmp.fees.labels.feeProfileId',
      dataIndex: 'feeProfileId',
      formItemProps: {
        required: true,
        getValueFromEvent: transformUppercaseNoSpaces,
      },
      fieldProps: {
        disabled: mode !== CrudMode.CREATE,
      },
    },
    {
      title: 'pmp.fees.labels.feeProfileName',
      dataIndex: 'feeProfileName',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        disabled: mode === CrudMode.VIEW,
      },
    },
    {
      title: 'pmp.fees.labels.feeDefinitions',
      dataIndex: 'feeIds',
      valueType: 'select',
      fieldProps: {
        mode: 'multiple',
        options: selectsOptions[FieldType.FEES],
        disabled,
      },
    },
  ];
};
